import React from 'react';
import { ArrowLeft } from 'lucide-react';

type LegalProps = {
    onBack: () => void;
};

export const TermsOfService: React.FC<LegalProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-[#0f172a] text-white p-8 overflow-y-auto">
            <div className="max-w-3xl mx-auto">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-neon-gold hover:text-white mb-8 transition-colors"
                >
                    <ArrowLeft size={20} />
                    Back to Home
                </button>

                <h1 className="text-3xl font-bold mb-2 text-neon-gold">Terms of Service — WeaverBI</h1>
                <p className="text-gray-400 mb-8">Last Updated: February 2025</p>

                <div className="space-y-8 text-gray-300 leading-relaxed">
                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
                        <p>By accessing or using WeaverBI (“the Service”), you agree to be bound by these Terms of Service (“Terms”). If you do not agree to these Terms, do not use the Service.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
                        <p>WeaverBI is a browser-based business intelligence and data visualization tool that allows users to upload datasets, create charts, and build dashboards.</p>
                        <p className="mt-2">The Service is currently in Beta and may include bugs, incomplete features, or downtime.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">3. User Accounts</h2>
                        <p>Some features require creating an account via Google OAuth or email.</p>
                        <p className="mt-2">You are responsible for:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Maintaining the confidentiality of your account</li>
                            <li>Activities performed through your account</li>
                        </ul>
                        <p className="mt-2">WeaverBI is not responsible for unauthorized access resulting from your actions.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">4. User Content</h2>
                        <p>You retain full ownership of any data you upload (“User Content”).</p>
                        <p className="mt-2">By using the Service, you grant WeaverBI a non-exclusive, worldwide, royalty-free license to store, process, and display your User Content solely to operate the Service.</p>
                        <p className="mt-2 font-medium text-neon-gold/80">Your content is never sold, shared, or used for advertising.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">5. Prohibited Conduct</h2>
                        <p>You agree not to:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Upload illegal or harmful content</li>
                            <li>Attempt to breach, hack, or reverse-engineer the Service</li>
                            <li>Use the Service for unlawful activities</li>
                            <li>Upload sensitive personal data (e.g., medical data, passwords, financial info)</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">6. “Beta” Disclaimer</h2>
                        <p>The Service may experience: errors, data loss, downtime, or incomplete features.</p>
                        <p className="mt-2">By using WeaverBI, you acknowledge these risks and agree that it is provided as an early-stage product.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">7. Disclaimer of Warranties</h2>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                            <p className="uppercase font-medium">THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND.</p>
                            <p className="uppercase font-medium mt-2">WEAVERBI DOES NOT GUARANTEE UNINTERRUPTED, SECURE, OR ERROR-FREE OPERATION.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">8. Limitation of Liability</h2>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                            <p className="uppercase font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW, WEAVERBI IS NOT LIABLE FOR:</p>
                            <ul className="list-disc pl-5 mt-2 space-y-1 uppercase text-sm">
                                <li>Lost profits</li>
                                <li>Lost data</li>
                                <li>Business interruption</li>
                                <li>Indirect or consequential damages</li>
                            </ul>
                            <p className="mt-4">Your sole remedy is to stop using the Service.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">9. Changes to Terms</h2>
                        <p>We may update these Terms periodically. Continued use of the Service constitutes acceptance of updated Terms.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">10. Contact</h2>
                        <p>For questions: <a href="mailto:weaverbi333@gmail.com" className="text-neon-gold hover:underline">weaverbi333@gmail.com</a></p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export const PrivacyPolicy: React.FC<LegalProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-[#0f172a] text-white p-8 overflow-y-auto">
            <div className="max-w-3xl mx-auto">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-neon-gold hover:text-white mb-8 transition-colors"
                >
                    <ArrowLeft size={20} />
                    Back to Home
                </button>

                <h1 className="text-3xl font-bold mb-2 text-neon-gold">Privacy Policy — WeaverBI</h1>
                <p className="text-gray-400 mb-8">Last Updated: February 2025</p>

                <div className="space-y-8 text-gray-300 leading-relaxed">
                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
                        <p>WeaverBI (“we,” “us,” “our”) is committed to protecting your privacy. This Policy explains what data we collect and how we use it.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>

                        <div className="mb-4">
                            <h3 className="text-lg font-medium text-white mb-2">Account Information</h3>
                            <p>If you sign up, we collect:</p>
                            <ul className="list-disc pl-5 mt-1 space-y-1">
                                <li>Email</li>
                                <li>Name</li>
                                <li>Profile picture (if provided via Google OAuth)</li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h3 className="text-lg font-medium text-white mb-2">User Content</h3>
                            <p>Datasets, charts, dashboards, and files you upload or generate.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-white mb-2">Usage Data</h3>
                            <p>Anonymous analytics such as:</p>
                            <ul className="list-disc pl-5 mt-1 space-y-1">
                                <li>Pages visited</li>
                                <li>Features used</li>
                                <li>Error logs</li>
                            </ul>
                            <p className="mt-1">This helps us improve performance and stability.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
                        <p>We use data to:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Provide and maintain the Service</li>
                            <li>Authenticate users</li>
                            <li>Improve the product</li>
                            <li>Fix bugs and enhance security</li>
                            <li>Communicate important updates</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">4. Data Sharing</h2>
                        <p>We do not sell or trade your personal information.</p>
                        <p className="mt-2">We may use trusted third-party services strictly for operating the platform, such as:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Hosting (Render)</li>
                            <li>Frontend deployment (Vercel)</li>
                            <li>Database (Render PostgreSQL)</li>
                        </ul>
                        <p className="mt-2">These providers have their own privacy and security measures.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">5. Data Security</h2>
                        <p>We implement reasonable security practices, including encrypted communications, secure cookies, and restricted server access.</p>
                        <p className="mt-2">However, no method of transmission or storage is 100% secure.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">6. Your Rights</h2>
                        <p>You may request to:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Access your data</li>
                            <li>Update your account information</li>
                            <li>Delete your account or data</li>
                        </ul>
                        <p className="mt-2">You may contact us at any time to make such requests.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">7. Children’s Privacy</h2>
                        <p>WeaverBI is not intended for users under 13.</p>
                        <p className="mt-2">We do not knowingly collect data from children.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">8. Changes to This Policy</h2>
                        <p>We may update this Privacy Policy as the Service evolves.</p>
                        <p className="mt-2">Changes will be posted on this page.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-white mb-3">9. Contact</h2>
                        <p>For privacy-related questions or requests:</p>
                        <p><a href="mailto:weaverbi333@gmail.com" className="text-neon-gold hover:underline">weaverbi333@gmail.com</a></p>
                    </section>
                </div>
            </div>
        </div>
    );
};
